import React, { createContext, useContext, useState, useEffect } from 'react';

export interface UserProfile {
  uid: string;
  email: string;
  displayName?: string;
  role: 'admin' | 'user';
  status: 'approved' | 'pending' | 'rejected';
  createdAt: Date;
  isFirstUser?: boolean;
  editableListings?: number[]; // Array of waterfall IDs this user can edit
}

interface AuthContextType {
  currentUser: { uid: string; email: string } | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signup: (email: string, password: string, displayName: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: () => boolean;
  isApproved: () => boolean;
  canEditListing: (listingId: number) => boolean;
  grantListingAccess: (userId: string, listingId: number) => Promise<void>;
  revokeListingAccess: (userId: string, listingId: number) => Promise<void>;
  getUserListingPermissions: (userId: string) => number[];
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Mock local storage functions
const getStoredUsers = (): UserProfile[] => {
  const stored = localStorage.getItem('mock_users');
  return stored ? JSON.parse(stored) : [];
};

const saveUsers = (users: UserProfile[]) => {
  localStorage.setItem('mock_users', JSON.stringify(users));
};

const getCurrentUser = () => {
  const stored = localStorage.getItem('mock_current_user');
  return stored ? JSON.parse(stored) : null;
};

const setCurrentUser = (user: any) => {
  if (user) {
    localStorage.setItem('mock_current_user', JSON.stringify(user));
  } else {
    localStorage.removeItem('mock_current_user');
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUserState] = useState<{ uid: string; email: string } | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const findUserProfile = (uid: string): UserProfile | null => {
    const users = getStoredUsers();
    return users.find(user => user.uid === uid) || null;
  };

  const signup = async (email: string, password: string, displayName: string) => {
    const users = getStoredUsers();
    const isFirstUser = users.length === 0;
    
    // Check if user already exists
    if (users.find(u => u.email === email)) {
      throw new Error('User with this email already exists');
    }

    const uid = 'user_' + Date.now();
    const newUser = { uid, email };
    
    const profile: UserProfile = {
      uid,
      email,
      displayName,
      role: isFirstUser ? 'admin' : 'user',
      status: isFirstUser ? 'approved' : 'pending',
      createdAt: new Date(),
      isFirstUser
    };

    users.push(profile);
    saveUsers(users);
    setCurrentUser(newUser);
    setCurrentUserState(newUser);
    setUserProfile(profile);
  };

  const login = async (email: string, password: string) => {
    const users = getStoredUsers();
    const user = users.find(u => u.email === email);
    
    if (!user) {
      throw new Error('No user found with this email');
    }

    const mockUser = { uid: user.uid, email: user.email };
    setCurrentUser(mockUser);
    setCurrentUserState(mockUser);
    setUserProfile(user);
  };

  const logout = async () => {
    setCurrentUser(null);
    setCurrentUserState(null);
    setUserProfile(null);
  };

  const isAdmin = () => {
    return userProfile?.role === 'admin' && userProfile?.status === 'approved';
  };

  const isApproved = () => {
    return userProfile?.status === 'approved';
  };

  const canEditListing = (listingId: number) => {
    // Admins can edit any listing
    if (isAdmin()) return true;
    // Regular users can only edit listings they have permission for
    return userProfile?.editableListings?.includes(listingId) || false;
  };

  const grantListingAccess = async (userId: string, listingId: number) => {
    const users = getStoredUsers();
    const updatedUsers = users.map(user => {
      if (user.uid === userId) {
        const editableListings = user.editableListings || [];
        if (!editableListings.includes(listingId)) {
          return { ...user, editableListings: [...editableListings, listingId] };
        }
      }
      return user;
    });
    saveUsers(updatedUsers);
    
    // Update current user if it's the same user
    if (userProfile?.uid === userId) {
      const updatedProfile = updatedUsers.find(u => u.uid === userId);
      if (updatedProfile) setUserProfile(updatedProfile);
    }
  };

  const revokeListingAccess = async (userId: string, listingId: number) => {
    const users = getStoredUsers();
    const updatedUsers = users.map(user => {
      if (user.uid === userId) {
        const editableListings = user.editableListings || [];
        return { ...user, editableListings: editableListings.filter(id => id !== listingId) };
      }
      return user;
    });
    saveUsers(updatedUsers);
    
    // Update current user if it's the same user
    if (userProfile?.uid === userId) {
      const updatedProfile = updatedUsers.find(u => u.uid === userId);
      if (updatedProfile) setUserProfile(updatedProfile);
    }
  };

  const getUserListingPermissions = (userId: string) => {
    const users = getStoredUsers();
    const user = users.find(u => u.uid === userId);
    return user?.editableListings || [];
  };

  // Load user on mount
  useEffect(() => {
    const user = getCurrentUser();
    if (user) {
      const profile = findUserProfile(user.uid);
      setCurrentUserState(user);
      setUserProfile(profile);
    }
    setLoading(false);
  }, []);

  // Save current user whenever it changes
  useEffect(() => {
    setCurrentUser(currentUser);
  }, [currentUser]);

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    signup,
    login,
    logout,
    isAdmin,
    isApproved,
    canEditListing,
    grantListingAccess,
    revokeListingAccess,
    getUserListingPermissions
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};